import type { HTMLAttributes, MouseEventHandler, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { autoPlacement, autoUpdate, offset, size, useFloating } from '@floating-ui/react';
import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faChevronDown, faChevronUp, faClose } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';

import { cn } from '../../lib/utils.ts';
import { FieldError } from '../FieldError';
import { FieldLabel } from '../FieldLabel/FieldLabel';

export interface SelectProps<TValue, TOption> {
    name?: string;
    id?: string;
    value: TValue;
    defaultValue?: TValue;
    placeholder: ReactNode;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    className?: ClassValue;
    buttonClassName?: ClassValue;
    onChange?: (value: TValue) => void;
    onDrop?: () => void;
    options: SelectOptionProps<TOption>[];
    label?: ReactNode; // Preferably a string for keeping styles consistent
    required?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    multiple?: boolean;
    formatOption?: ({
        value,
        selected,
        active,
        text,
    }: {
        value: TOption;
        selected?: boolean;
        active?: boolean;
        text?: string;
    }) => ReactNode;
    icon?: IconDefinition;
    enableDropSelection?: boolean;
    headerContent?: ReactNode;
    showChevron?: boolean;
}

export interface SelectOptionProps<TOption> {
    id: string;
    value: TOption;
    disabled?: boolean;
    text?: string;
}

export const Select = <TValue extends TOption | TOption[], TOption>({
    placeholder,
    options = [],
    name,
    id,
    value,
    defaultValue,
    onChange,
    onDrop,
    dataTestid,
    htmlAttributes,
    className,
    buttonClassName,
    label,
    required = false,
    disabled = false,
    errorMessage,
    multiple = false,
    formatOption = ({ value, text }) => <>{text ?? (value as ReactNode)}</>,
    icon,
    enableDropSelection = false,
    headerContent,
    showChevron = true,
}: SelectProps<TValue, TOption>) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const { refs, floatingStyles } = useFloating({
        middleware: [
            offset(2),
            autoPlacement({
                allowedPlacements: ['bottom-start', 'bottom-end'],
            }),
            size({
                apply({ elements, availableHeight }) {
                    const referenceRect = elements.reference.getBoundingClientRect();
                    const floatingElement = elements.floating;

                    floatingElement.style.width = 'max-content';
                    const floatingWidth = floatingElement.getBoundingClientRect().width;

                    if (floatingWidth >= referenceRect.width) {
                        floatingElement.style.width = 'max-content';
                    } else {
                        floatingElement.style.width = `${referenceRect.width}px`;
                    }
                    floatingElement.style.maxHeight = `${Math.min(availableHeight - 10, 300)}px`;
                    floatingElement.style.overflowY = 'auto';
                },
            }),
        ],
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
    });

    const toggleDropdown: MouseEventHandler = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    return (
        <div
            data-testid={dataTestid}
            data-component-name='Select'
            ref={containerRef}
            {...htmlAttributes}
            className={cn(className, {
                'cursor-not-allowed': disabled,
            })}
        >
            <Listbox
                value={value}
                defaultValue={defaultValue}
                onChange={(val) => {
                    onChange?.(val);
                    if (!multiple) {
                        setIsOpen(false);
                    }
                }}
                multiple={multiple}
                name={name}
            >
                {label && (
                    <FieldLabel
                        className={clsx('mb-2', { 'ml-9': icon })}
                        label={label}
                        required={required}
                        htmlFor={id || name}
                    />
                )}

                <div className='flex gap-3'>
                    {icon && (
                        <FontAwesomeIcon
                            className={clsx('size-[24px] text-gray-500', {
                                'pt-[8px]': label,
                            })}
                            icon={icon}
                        />
                    )}
                    <div
                        className='relative w-full'
                        role='select'
                        ref={refs.setReference}
                    >
                        <ListboxButton
                            id={id || name}
                            ref={buttonRef}
                            data-testid={`${dataTestid}-button`}
                            disabled={disabled}
                            className={cn(
                                'flex w-full items-stretch justify-between gap-2 whitespace-nowrap rounded-md border border-solid border-gray-300 p-2 text-sm',
                                'disabled:pointer-events-none disabled:bg-gray-100 disabled:text-gray-500',
                                {
                                    'border-red-300': !!errorMessage,
                                    'cursor-pointer': !showChevron && !disabled,
                                },
                                buttonClassName
                            )}
                            onClick={() => {
                                setIsOpen(!isOpen);
                            }}
                        >
                            <div
                                className={clsx('overflow-hidden text-ellipsis whitespace-nowrap', {
                                    'text-gray-500': !value,
                                })}
                            >
                                {placeholder}
                            </div>
                            {showChevron && (
                                <div className='flex items-center border-l border-solid border-gray-300'>
                                    <FontAwesomeIcon
                                        icon={
                                            enableDropSelection && value
                                                ? faClose
                                                : isOpen
                                                  ? faChevronUp
                                                  : faChevronDown
                                        }
                                        className='p-1 pl-3'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (enableDropSelection && value && onDrop) {
                                                onDrop();
                                            } else {
                                                toggleDropdown(e);
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </ListboxButton>
                        {isOpen && (
                            <div
                                className='z-dropdown w-full overflow-y-auto rounded-md border border-gray-300 bg-white text-sm text-gray-800 shadow-lg'
                                ref={refs.setFloating}
                                style={{ ...floatingStyles }}
                            >
                                <ListboxOptions static>
                                    {headerContent && (
                                        <div className='border-b border-solid border-gray-300'>{headerContent}</div>
                                    )}
                                    {options.map(
                                        (
                                            {
                                                id: optionId,
                                                value: optionValue,
                                                disabled = false,
                                                text,
                                            }: SelectOptionProps<TOption>,
                                            index: number
                                        ) => (
                                            <ListboxOption
                                                key={
                                                    dataTestid
                                                        ? `${dataTestid}-${optionId}`
                                                        : `Option-${name}-${optionId || index}`
                                                }
                                                value={optionValue}
                                                disabled={disabled}
                                                className={clsx('block w-full text-left text-sm', {
                                                    'text-gray-500 cursor-not-allowed': disabled,
                                                })}
                                                as='button'
                                                type='button'
                                            >
                                                {({ selected, focus }) => (
                                                    <div
                                                        className={clsx('p-2 px-4', {
                                                            'bg-gray-200': selected && !multiple,
                                                            'bg-gray-100': focus,
                                                            'hover:bg-gray-100': !(selected && !multiple),
                                                        })}
                                                    >
                                                        {formatOption({
                                                            value: optionValue,
                                                            selected: selected,
                                                            active: focus,
                                                            text,
                                                        })}
                                                    </div>
                                                )}
                                            </ListboxOption>
                                        )
                                    )}
                                </ListboxOptions>
                            </div>
                        )}
                    </div>
                </div>
            </Listbox>

            {errorMessage && (
                <FieldError
                    className={clsx({ 'ml-9': !!icon })}
                    dataTestid={dataTestid && `${dataTestid}-error`}
                >
                    {errorMessage}
                </FieldError>
            )}
        </div>
    );
};
